import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useStyles } from '~/assets/styles';
import { ClaimContextProvider } from '~/components/ClaimContainer';
import mixpanel from '~/components/CmsMain/mixpanel';
import SendSmsCommunicationCardContainer from '~/components/communications/SmsCommunicationCard/SmsCommunicationCard';
import CommunicationPopoverLoader from '~/components/Contacts/ContactCommunications/CommunicationPopoverLoader';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import { useCms } from '~/components/hooks/useCms';
import SmsIcon from '~/components/icons/notifications/SmsIcon';
import InlineIconButton from '~/components/InlineIconButton';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

// Whatever stops this popover's trigger from opening the composer, for a caller that opens it itself.
export const isSmsCommunicationDisabled = (user, contact) =>
  !user.is_sending_sms_allowed || contact.is_smsing_allowed === false || user.role.is_view_only;

// Opened from its own trigger button, unless the caller passes `isOpen`: then no trigger renders, the
// composer shows while `isOpen` is true, and closing it calls `onClose`.
const SmsCommunicationPopover = ({
  contact,
  onUpdate,
  communicationDetails = {},
  mixpanelSource,
  ignorePermissions = false,
  useIconButton = false,
  ariaLabel,
  messageContent,
  claimId,
  isOpen,
  onClose,
}) => {
  const { classes } = useStyles();
  const { user } = useCms();
  const [isDialogOpenState, setIsDialogOpen] = useState(false);
  const isControlled = isOpen !== undefined;
  const isDialogOpen = isControlled ? isOpen : isDialogOpenState;

  // The trigger tracks its click; a caller that opens the composer itself gets the same event on open.
  useEffect(() => {
    if (isControlled && isOpen) {
      mixpanel.track(MIXPANEL_EVENTS.NEW_SMS_COMMUNICATION_CLICKED, { source: mixpanelSource });
    }
  }, [isControlled, isOpen, mixpanelSource]);

  const [claim, isLoadingClaim, isError, onClaimUpdate] = useFetchClaim(claimId);
  const handleOpenDialog = () => {
    mixpanel.track(MIXPANEL_EVENTS.NEW_SMS_COMMUNICATION_CLICKED, { source: mixpanelSource });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setIsDialogOpen(false);
    }
  };

  const isDisabled = isSmsCommunicationDisabled(user, contact);
  return (
    <>
      {isControlled ? null : (
        <InlineIconButton
          icon={SmsIcon}
          onClick={handleOpenDialog}
          disabled={isDisabled}
          className={cn({ [classes.hoverableNonFilledIcon]: !isDisabled })}
          ignorePermissions={ignorePermissions}
          useIconButton={useIconButton}
          ariaLabel={ariaLabel}
        />
      )}
      {isDialogOpen && (
        <LoadingSwitch isLoading={isLoadingClaim} isError={isError} LoadingComponent={CommunicationPopoverLoader}>
          <ClaimContextProvider refreshData={onClaimUpdate} claim={claim}>
            <SendSmsCommunicationCardContainer
              contact={contact}
              contactPhoneId={communicationDetails.id}
              onCancel={handleCloseDialog}
              onSendSms={() => {
                handleCloseDialog();
                if (onUpdate) {
                  onUpdate();
                }
              }}
              messageContent={messageContent}
            />
          </ClaimContextProvider>
        </LoadingSwitch>
      )}
    </>
  );
};

SmsCommunicationPopover.propTypes = {
  contact: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
  communicationDetails: PropTypes.object,
  mixpanelSource: PropTypes.string,
  ignorePermissions: PropTypes.bool,
  useIconButton: PropTypes.bool,
  ariaLabel: PropTypes.string,
  messageContent: PropTypes.string,
  claimId: PropTypes.number,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default SmsCommunicationPopover;
